import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button, Checkbox, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select, Spin,
} from 'antd';
import GraficaCard, {
  AGREGACIONES_AGRUPABLES_EN_OTROS, AGRUPACIONES_FECHA, ETIQUETAS_AGREGACION, MODULOS,
  TIPOS_DE_UNA_SOLA_METRICA, TIPOS_GRAFICA,
} from 'components/GraficaConfigurableCard';
import DescribirGraficaConPalabras from 'components/DescribirGraficaConPalabras';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import API from 'utils/api';
import { onError, onSuccess } from 'utils/handlers';
import { isAdmin } from 'utils/permission';
import { normalizar } from 'utils/traductorGraficas';

const { Option } = Select;

const OPERADORES_POR_TIPO = {
  fecha: [
    { value: 'exact', label: 'Es igual a (YYYY-MM-DD)' },
    { value: 'gte', label: 'Desde (YYYY-MM-DD)' },
    { value: 'lte', label: 'Hasta (YYYY-MM-DD)' },
  ],
  numerico: [
    { value: 'exact', label: 'Es igual a' },
    { value: 'gte', label: 'Mayor o igual a' },
    { value: 'lte', label: 'Menor o igual a' },
    { value: 'gt', label: 'Mayor que' },
  ],
  categoria: [
    { value: 'exact', label: 'Es igual a' },
    { value: 'in', label: 'Está en (elige varios)' },
  ],
  texto: [{ value: 'icontains', label: 'Contiene' }],
  booleano: [{ value: 'exact', label: 'Es' }],
};

const ConstructorDeGraficas = () => {
  const esAdmin = isAdmin();
  const [modulo, setModulo] = useState(MODULOS[0]?.value);
  const [catalogo, setCatalogo] = useState([]);
  const [graficas, setGraficas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [graficaEditando, setGraficaEditando] = useState(null);
  const [modeloAncla, setModeloAncla] = useState(null);
  const [valoresPorCampo, setValoresPorCampo] = useState({});
  const [cargandoValoresDe, setCargandoValoresDe] = useState(null);
  const [form] = Form.useForm();

  // Se pide el catálogo completo (sin filtrar por módulo): una gráfica de un
  // módulo puede usar campos etiquetados en otro (p. ej. "Género" en Nómina),
  // y al editarla esos campos deben seguir apareciendo en los selectores.
  // El filtrado por módulo se hace en el cliente solo mientras no hay modelo ancla.
  const fetchCatalogo = useCallback(async () => {
    if (!esAdmin) return;
    try {
      const res = await API.get('configuraciones/catalogo-metricas/');
      setCatalogo(res?.data || []);
    } catch (err) {
      onError(err);
    }
  }, [esAdmin]);

  const fetchGraficas = useCallback(async () => {
    try {
      setLoading(true);
      // Los administradores también ven aquí las gráficas ocultas del Dashboard.
      const params = { modulo, incluir_ocultas: esAdmin ? 1 : undefined };
      const res = await API.get('configuraciones/configuraciones-graficas/', { params });
      setGraficas(res?.data || []);
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  }, [modulo, esAdmin]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      fetchCatalogo();
      fetchGraficas();
    }
    return () => { mounted = false; };
  }, [fetchCatalogo, fetchGraficas]);

  const todasDimensiones = catalogo.filter((c) => c.es_dimension);
  const todasMetricas = catalogo.filter((c) => c.es_metrica);
  const todosFecha = catalogo.filter((c) => c.tipo_dato === 'fecha');
  const delModulo = (c) => c.modulo === modulo;

  const recalcularAncla = () => {
    const dimId = form.getFieldValue('dimension');
    const dimension = todasDimensiones.find((d) => d.id === dimId);
    if (dimension) {
      setModeloAncla(dimension.modelo_app_label);
      return;
    }
    const filasMetricas = form.getFieldValue('metricas') || [];
    const metricaEncontrada = filasMetricas
      .map((fila) => todasMetricas.find((m) => m.id === fila?.metrica))
      .find(Boolean);
    setModeloAncla(metricaEncontrada?.modelo_app_label || null);
  };

  const dimensionesDisponibles = modeloAncla
    ? todasDimensiones.filter((c) => c.modelo_app_label === modeloAncla)
    : todasDimensiones.filter(delModulo);
  const metricasDisponibles = modeloAncla
    ? todasMetricas.filter((c) => c.modelo_app_label === modeloAncla)
    : todasMetricas.filter(delModulo);
  const camposFechaDisponibles = modeloAncla
    ? todosFecha.filter((c) => c.modelo_app_label === modeloAncla)
    : [];
  const camposFiltrables = modeloAncla
    ? catalogo.filter((c) => c.modelo_app_label === modeloAncla)
    : [];

  // Regresa los valores reales de un campo de categoría (y los guarda para los selectores).
  const fetchValoresCategoria = useCallback(async (campoId) => {
    if (!campoId) return [];
    if (valoresPorCampo[campoId]) return valoresPorCampo[campoId];
    try {
      setCargandoValoresDe(campoId);
      const res = await API.get(`configuraciones/catalogo-metricas/${campoId}/valores/`);
      const valores = res?.data?.valores || [];
      setValoresPorCampo((prev) => ({ ...prev, [campoId]: valores }));
      return valores;
    } catch (err) {
      setValoresPorCampo((prev) => ({ ...prev, [campoId]: [] }));
      return [];
    } finally {
      setCargandoValoresDe(null);
    }
  }, [valoresPorCampo]);

  // Llena el formulario con lo que entendió el traductor de frases. Regresa avisos
  // extra ([tipo, mensaje]) para mostrarlos bajo el cuadro de la frase.
  const aplicarFrase = async ({ config: c }) => {
    const avisos = [];
    const filtros = await Promise.all(c.filtros.map(async (f) => {
      const { entrada } = f;
      let { valor } = f;
      if (entrada.tipo_dato === 'booleano') valor = valor ? 'true' : 'false';
      if (entrada.tipo_dato === 'fecha') valor = moment(valor, 'YYYY-MM-DD');
      if (entrada.tipo_dato === 'categoria') {
        const valores = await fetchValoresCategoria(entrada.id);
        // Un nombre escrito en la frase ("Oficialía Mayor") se cambia por el valor exacto.
        if (f.buscarValorExacto) {
          const buscado = normalizar(valor);
          const exacto = valores.find((v) => normalizar(v) === buscado)
            || valores.find((v) => normalizar(v).startsWith(buscado))
            || valores.find((v) => normalizar(v).includes(buscado));
          if (exacto) {
            if (exacto !== valor) avisos.push(['info', `Elegí «${exacto}» para «${valor}».`]);
            valor = exacto;
          } else {
            avisos.push(['warning', `No encontré «${valor}» en «${entrada.etiqueta_visible}»; `
              + 'elige el valor de la lista.']);
          }
        }
      }
      return { campo: entrada.id, operador: f.operador, valor };
    }));
    form.setFieldsValue({
      ...(graficaEditando ? {} : { modulo_destino: c.modulo }),
      nombre: c.nombre,
      tipo_grafica: c.tipo,
      dimension: c.dimension.id,
      agrupacion_fecha: c.agrupacionFecha || 'dia',
      ancho_rango: c.anchoRango || undefined,
      metricas: c.metricas.map((m) => ({ metrica: m.entrada.id, agregacion: m.agg })),
      campo_fecha_filtro: c.campoFecha ? c.campoFecha.id : undefined,
      filtro_fecha_por_defecto: c.filtroFechaPorDefecto,
      orden: c.orden,
      limite: c.limite || undefined,
      agrupar_resto_en_otros: c.otros,
      filtros,
    });
    setModeloAncla(c.ancla);
    return avisos;
  };

  const abrirModalNueva = () => {
    form.resetFields();
    setGraficaEditando(null);
    setModeloAncla(null);
    setShowModal(true);
  };

  const abrirModalEditar = (grafica) => {
    const ancla = grafica.dimension_detalle?.modelo_app_label
      || grafica.metricas?.[0]?.metrica_detalle?.modelo_app_label
      || null;
    // Los filtros se guardan con la ruta del campo (p. ej. "genero__descripcion"),
    // no con el id del catálogo — se traducen de vuelta al id para el selector.
    const filtros = (grafica.filtros || []).map((f) => {
      const campoCat = catalogo.find((c) => c.campo === f.campo && c.modelo_app_label === ancla);
      if (campoCat?.tipo_dato === 'categoria') fetchValoresCategoria(campoCat.id);
      let { valor } = f;
      if (campoCat?.tipo_dato === 'fecha' && valor) valor = moment(valor, 'YYYY-MM-DD');
      return { campo: campoCat?.id ?? f.campo, operador: f.operador, valor };
    });

    form.resetFields();
    form.setFieldsValue({
      nombre: grafica.nombre,
      tipo_grafica: grafica.tipo_grafica,
      dimension: grafica.dimension,
      metricas: (grafica.metricas || []).map((m) => ({
        metrica: m.metrica,
        agregacion: m.agregacion,
      })),
      campo_fecha_filtro: grafica.campo_fecha_filtro || undefined,
      filtro_fecha_por_defecto: grafica.filtro_fecha_por_defecto || 'ninguno',
      orden: grafica.orden || 'valor_desc',
      agrupacion_fecha: grafica.agrupacion_fecha || 'dia',
      ancho_rango: grafica.ancho_rango || undefined,
      limite: grafica.limite || undefined,
      agrupar_resto_en_otros: !!grafica.agrupar_resto_en_otros,
      visible_en_dashboard: grafica.visible_en_dashboard !== false,
      filtros,
    });
    setGraficaEditando(grafica);
    setModeloAncla(ancla);
    setShowModal(true);
  };

  const onGuardar = async (values) => {
    try {
      const filtros = (values.filtros || []).map((f) => {
        const campoCatalogo = camposFiltrables.find((c) => c.id === f.campo);
        let { valor } = f;
        if (campoCatalogo?.tipo_dato === 'fecha' && valor?.format) valor = valor.format('YYYY-MM-DD');
        if (f.operador === 'in' && !Array.isArray(valor)) valor = String(valor).split(',').map((v) => v.trim());
        // Si el filtro guardado apunta a un campo que ya no está en el catálogo,
        // se conserva su ruta original en vez de perderlo.
        return { campo: campoCatalogo?.campo || f.campo, operador: f.operador, valor };
      });
      const metricas = (values.metricas || []).map((m) => ({
        metrica: m.metrica,
        agregacion: m.agregacion,
      }));
      const payload = {
        nombre: values.nombre,
        modulo: graficaEditando?.modulo || values.modulo_destino || modulo,
        tipo_grafica: values.tipo_grafica,
        dimension: values.dimension,
        metricas,
        campo_fecha_filtro: values.campo_fecha_filtro || null,
        filtro_fecha_por_defecto: values.campo_fecha_filtro
          ? (values.filtro_fecha_por_defecto || 'ninguno')
          : 'ninguno',
        orden: values.orden || 'valor_desc',
        // Solo aplica a dimensiones de fecha; el backend rechaza otra cosa.
        agrupacion_fecha: todosFecha.some((c) => c.id === values.dimension)
          ? (values.agrupacion_fecha || 'dia')
          : 'dia',
        // Solo aplica a dimensiones numéricas; el backend rechaza otra cosa.
        ancho_rango: todasDimensiones.some((c) => c.id === values.dimension && c.tipo_dato === 'numerico')
          ? (values.ancho_rango || null)
          : null,
        limite: values.limite || null,
        agrupar_resto_en_otros: !!(values.limite && values.agrupar_resto_en_otros),
        visible_en_dashboard: values.visible_en_dashboard !== false,
        filtros,
      };
      if (graficaEditando) {
        await API.put(`configuraciones/configuraciones-graficas/${graficaEditando.id}/`, payload);
        onSuccess({}, 'Gráfica actualizada correctamente');
      } else {
        await API.post('configuraciones/configuraciones-graficas/', payload);
        onSuccess({}, 'Gráfica creada correctamente');
      }
      setShowModal(false);
      // Si se guardó en otra sección, se cambia a esa para que se vea (el cambio recarga la lista).
      if (payload.modulo !== modulo) setModulo(payload.modulo);
      else fetchGraficas();
    } catch (err) {
      onError(err, null, [form]);
    }
  };

  const onCambiarVisibilidad = async (grafica, visible) => {
    try {
      await API.put(`configuraciones/configuraciones-graficas/${grafica.id}/`, {
        visible_en_dashboard: visible,
      });
      setGraficas((prev) => prev.map((g) => (
        g.id === grafica.id ? { ...g, visible_en_dashboard: visible } : g
      )));
      onSuccess({}, visible
        ? `"${grafica.nombre}" ya se ve en el Dashboard`
        : `"${grafica.nombre}" se ocultó del Dashboard`);
    } catch (err) {
      onError(err);
    }
  };

  const onEliminarGrafica = (id) => {
    setGraficas((prev) => prev.filter((g) => g.id !== id));
  };

  const tipoGraficaSeleccionado = Form.useWatch('tipo_grafica', form);
  const metricasSeleccionadas = Form.useWatch('metricas', form) || [];
  const soloUnaMetrica = TIPOS_DE_UNA_SOLA_METRICA.includes(tipoGraficaSeleccionado);

  return (
    <Row className="container free-scroll-page">
      <Spin spinning={loading} style={{ width: '100%' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col><h2>Constructor de Gráficas</h2></Col>
          <Col style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {MODULOS.length > 1 && (
              <Select value={modulo} onChange={setModulo} style={{ width: 200 }}>
                {MODULOS.map((m) => <Option key={m.value} value={m.value}>{m.label}</Option>)}
              </Select>
            )}
            {esAdmin && (
              <Button type="primary" icon={<PlusOutlined />} onClick={abrirModalNueva}>
                Nueva Gráfica
              </Button>
            )}
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          {graficas.map((grafica) => (
            <Col key={grafica.id} xs={24} md={12} xl={8}>
              <GraficaCard
                grafica={grafica}
                esAdmin={esAdmin}
                onEditar={esAdmin ? abrirModalEditar : null}
                onCambiarVisibilidad={esAdmin ? onCambiarVisibilidad : null}
                onDeleted={onEliminarGrafica}
              />
            </Col>
          ))}
        </Row>

        <Modal
          title={graficaEditando ? `Editar Gráfica: ${graficaEditando.nombre}` : 'Nueva Gráfica'}
          open={showModal}
          onCancel={() => setShowModal(false)}
          onOk={() => form.submit()}
          width={700}
          destroyOnClose
        >
          <DescribirGraficaConPalabras catalogo={catalogo} onAplicar={aplicarFrase} />

          <Form form={form} layout="vertical" onFinish={onGuardar}>
            {!graficaEditando && (
              <Form.Item name="modulo_destino" label="Sección del Dashboard" initialValue={modulo}>
                <Select>
                  {MODULOS.map((m) => <Option key={m.value} value={m.value}>{m.label}</Option>)}
                </Select>
              </Form.Item>
            )}

            <Form.Item name="nombre" label="Nombre" rules={[{ required: true, message: 'Requerido' }]}>
              <Input />
            </Form.Item>

            <Form.Item
              name="visible_en_dashboard"
              valuePropName="checked"
              initialValue
              extra="Desmárcala para armarla y revisarla aquí antes de que la vean los demás."
            >
              <Checkbox>Mostrar en el Dashboard</Checkbox>
            </Form.Item>

            <Form.Item
              name="tipo_grafica"
              label="Tipo de Gráfica"
              initialValue="barra"
              rules={[{ required: true }]}
            >
              <Select>
                {TIPOS_GRAFICA.map((t) => <Option key={t.value} value={t.value}>{t.label}</Option>)}
              </Select>
            </Form.Item>

            <Form.Item
              name="dimension"
              label="Dimensión (eje X)"
              rules={[{ required: true, message: 'Requerido' }]}
            >
              <Select showSearch optionFilterProp="children" onChange={recalcularAncla}>
                {dimensionesDisponibles.map((c) => (
                  <Option key={c.id} value={c.id}>{c.etiqueta_visible}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item shouldUpdate noStyle>
              {() => {
                const dimensionId = form.getFieldValue('dimension');
                if (!todosFecha.some((c) => c.id === dimensionId)) return null;
                return (
                  <Form.Item
                    name="agrupacion_fecha"
                    label="Agrupar la fecha"
                    initialValue="dia"
                    extra="Por mes o por año da una barra por periodo en vez de una por cada día."
                  >
                    <Select>
                      {AGRUPACIONES_FECHA.map((a) => (
                        <Option key={a.value} value={a.value}>{a.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Form.Item shouldUpdate noStyle>
              {() => {
                const dimensionId = form.getFieldValue('dimension');
                const esNumerica = todasDimensiones.some(
                  (c) => c.id === dimensionId && c.tipo_dato === 'numerico',
                );
                if (!esNumerica) return null;
                return (
                  <Form.Item
                    name="ancho_rango"
                    label="Agrupar en rangos de (opcional)"
                    extra={'Ej. 5 en edad da "20 a 24", "25 a 29"…; 5000 en sueldo da "5,000 a 9,999.99"…. '
                      + 'Vacío = una barra por cada valor. Usa orden "Alfabético" para verlos de menor a mayor.'}
                  >
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Form.List name="metricas">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <Row key={field.key} gutter={8} align="middle">
                      <Col span={11}>
                        <Form.Item
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...field}
                          name={[field.name, 'metrica']}
                          label="Métrica"
                          rules={[{ required: true, message: 'Requerido' }]}
                        >
                          <Select
                            showSearch
                            optionFilterProp="children"
                            onChange={recalcularAncla}
                            disabled={metricasSeleccionadas.length > 1 && soloUnaMetrica}
                          >
                            {metricasDisponibles.map((c) => (
                              <Option key={c.id} value={c.id}>{c.etiqueta_visible}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={11}>
                        <Form.Item shouldUpdate noStyle>
                          {() => {
                            const metricaId = form.getFieldValue(['metricas', field.name, 'metrica']);
                            const metricaCat = metricasDisponibles.find((c) => c.id === metricaId);
                            return (
                              <Form.Item
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...field}
                                name={[field.name, 'agregacion']}
                                label="Agregación"
                                rules={[{ required: true, message: 'Requerido' }]}
                              >
                                <Select>
                                  {(metricaCat?.agregaciones_permitidas || []).map((a) => (
                                    <Option key={a} value={a}>
                                      {ETIQUETAS_AGREGACION[a] ? `${a} · ${ETIQUETAS_AGREGACION[a]}` : a}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <DeleteOutlined onClick={() => remove(field.name)} />
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      block
                      disabled={fields.length >= 1 && soloUnaMetrica}
                      onClick={() => add()}
                    >
                      + Agregar Métrica
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Form.Item name="campo_fecha_filtro" label="Campo de fecha filtrable por el usuario (opcional)">
              <Select allowClear>
                {camposFechaDisponibles.map((c) => (
                  <Option key={c.id} value={c.id}>{c.etiqueta_visible}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item shouldUpdate noStyle>
              {() => {
                const campoFechaFiltro = form.getFieldValue('campo_fecha_filtro');
                if (!campoFechaFiltro) return null;
                return (
                  <Form.Item
                    name="filtro_fecha_por_defecto"
                    label="Rango por defecto (si el usuario no elige uno)"
                    initialValue="ninguno"
                  >
                    <Select>
                      <Option value="ninguno">Sin filtro (histórico completo)</Option>
                      <Option value="ultimo_periodo_calculado">Último periodo de nómina calculado</Option>
                    </Select>
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Row gutter={8}>
              <Col span={12}>
                <Form.Item name="orden" label="Orden" initialValue="valor_desc">
                  <Select>
                    <Option value="valor_desc">Mayor a menor</Option>
                    <Option value="valor_asc">Menor a mayor</Option>
                    <Option value="etiqueta_asc">Alfabético</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="limite" label="Límite (Top N, opcional)">
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item shouldUpdate noStyle>
              {() => {
                const limite = form.getFieldValue('limite');
                const filasMetricas = form.getFieldValue('metricas') || [];
                const todasAgrupables = filasMetricas.length > 0 && filasMetricas.every(
                  (fila) => AGREGACIONES_AGRUPABLES_EN_OTROS.includes(fila?.agregacion),
                );
                if (!limite || !todasAgrupables) return null;
                return (
                  <Form.Item name="agrupar_resto_en_otros" valuePropName="checked" initialValue={false}>
                    <Checkbox>
                      Agrupar el resto en &quot;Otros&quot; en vez de solo ocultarlo
                    </Checkbox>
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Form.List name="filtros">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <Row key={field.key} gutter={8} align="middle">
                      <Col span={8}>
                        <Form.Item
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...field}
                          name={[field.name, 'campo']}
                          label="Filtrar por"
                          rules={[{ required: true, message: 'Requerido' }]}
                        >
                          <Select
                            showSearch
                            optionFilterProp="children"
                            onChange={(campoId) => {
                              const campoCat = camposFiltrables.find((c) => c.id === campoId);
                              if (campoCat?.tipo_dato === 'categoria') fetchValoresCategoria(campoId);
                            }}
                          >
                            {camposFiltrables.map((c) => (
                              <Option key={c.id} value={c.id}>{c.etiqueta_visible}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={7}>
                        <Form.Item shouldUpdate noStyle>
                          {() => {
                            const campoId = form.getFieldValue(['filtros', field.name, 'campo']);
                            const campoCat = camposFiltrables.find((c) => c.id === campoId);
                            const operadores = OPERADORES_POR_TIPO[campoCat?.tipo_dato] || [];
                            return (
                              <Form.Item
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...field}
                                name={[field.name, 'operador']}
                                label="Operador"
                                rules={[{ required: true, message: 'Requerido' }]}
                              >
                                <Select>
                                  {operadores.map((op) => (
                                    <Option key={op.value} value={op.value}>{op.label}</Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </Col>
                      <Col span={7}>
                        <Form.Item shouldUpdate noStyle>
                          {() => {
                            const campoId = form.getFieldValue(['filtros', field.name, 'campo']);
                            const operador = form.getFieldValue(['filtros', field.name, 'operador']);
                            const campoCat = camposFiltrables.find((c) => c.id === campoId);
                            const tipoDato = campoCat?.tipo_dato;

                            let input;
                            if (tipoDato === 'booleano') {
                              input = (
                                <Select>
                                  <Option value="true">Sí</Option>
                                  <Option value="false">No</Option>
                                </Select>
                              );
                            } else if (tipoDato === 'fecha') {
                              input = <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />;
                            } else if (tipoDato === 'numerico') {
                              input = <InputNumber style={{ width: '100%' }} />;
                            } else if (tipoDato === 'categoria') {
                              input = (
                                <Select
                                  mode={operador === 'in' ? 'multiple' : undefined}
                                  showSearch
                                  loading={cargandoValoresDe === campoId}
                                  notFoundContent={cargandoValoresDe === campoId ? 'Cargando...' : 'Sin valores'}
                                >
                                  {(valoresPorCampo[campoId] || []).map((v) => (
                                    <Option key={v} value={v}>{v}</Option>
                                  ))}
                                </Select>
                              );
                            } else {
                              input = <Input />;
                            }

                            return (
                              <Form.Item
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...field}
                                name={[field.name, 'valor']}
                                label="Valor"
                                rules={[{ required: true, message: 'Requerido' }]}
                              >
                                {input}
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <DeleteOutlined onClick={() => remove(field.name)} />
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button type="dashed" block disabled={!modeloAncla} onClick={() => add()}>
                      + Agregar Filtro
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form>
        </Modal>
      </Spin>
    </Row>
  );
};

export default ConstructorDeGraficas;
